/**
 * Types pour le workflow de retrait amélioré
 */

// ============================================================================
// CONSTANTES
// ============================================================================

/** Montants rapides prédéfinis (FCFA) */
export const QUICK_AMOUNTS = [10000, 25000, 50000, 100000, 200000, 500000] as const;

/** Seuils d'alerte */
export const WITHDRAWAL_THRESHOLDS = {
  /** Montant nécessitant une confirmation supplémentaire */
  highAmountWarning: 500000,
  /** Montant nécessitant validation superviseur */
  supervisorRequired: 2000000,
  /** Plafond journalier par défaut */
  dailyLimit: 5000000,
  /** Plafond par transaction par défaut */
  transactionLimit: 2000000,
  /** Timeout de session en ms (5 minutes) */
  sessionTimeout: 5 * 60 * 1000,
  /** Avertissement timeout en ms (3 minutes) */
  timeoutWarning: 3 * 60 * 1000,
  /** Nombre max de tentatives biométriques */
  maxBiometricAttempts: 3,
} as const;

// ============================================================================
// TYPES
// ============================================================================

/** Type de destination du retrait */
export type WithdrawalDestination = 'self' | 'external';

/** Informations du bénéficiaire externe */
export interface ExternalRecipientInfo {
  name: string;
  phone: string;
  idNumber: string;
  idType?: string;
  relation?: string;
}

/** Données de confirmation du retrait */
export interface WithdrawalConfirmationData {
  accountNumber: string;
  accountNumberMasked: string;
  holderName: string;
  amount: number;
  denomination: Map<number, number>;
  destination: WithdrawalDestination;
  destinationLabel: string;
  externalRecipient?: ExternalRecipientInfo;
  notes?: string;
  // Infos de contexte
  currentBalance: number;
  balanceAfter: number;
  dailyLimitRemaining: number;
}

/** État du compteur de tentatives biométriques */
export interface BiometricAttemptState {
  current: number;
  max: number;
  remaining: number;
  isLocked: boolean;
}

/** Props pour le sélecteur de montants rapides */
export interface QuickAmountSelectorProps {
  amounts?: readonly number[];
  maxAmount: number;
  selectedAmount: number | null;
  onSelect: (amount: number) => void;
  disabled?: boolean;
}

/** Props pour l'affichage du solde */
export interface BalanceDisplayProps {
  balance: number;
  dailyLimit: number;
  dailyUsed: number;
  transactionLimit: number;
  recentWithdrawals?: Array<{
    date: Date;
    amount: number;
    reference: string;
  }>;
  onContinue: () => void;
}

/** Props pour la barre de contexte */
export interface WithdrawalContextBarProps {
  accountNumber: string;
  holderName: string;
  balance?: number;
  showBalance?: boolean;
  currentStep: number;
  totalSteps: number;
}

/** Props pour l'avertissement timeout */
export interface TimeoutWarningProps {
  remainingSeconds: number;
  onContinue: () => void;
  onCancel: () => void;
}

/** Props pour la confirmation de gros montant */
export interface HighAmountWarningProps {
  amount: number;
  threshold: number;
  onConfirm: () => void;
  onModify: () => void;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Masque un numéro de compte pour l'affichage
 * Ex: "001234567890" → "0012 **** 7890"
 */
export function maskAccountNumber(accountNumber: string): string {
  if (accountNumber.length < 8) return accountNumber;
  const start = accountNumber.slice(0, 4);
  const end = accountNumber.slice(-4);
  return `${start} **** ${end}`;
}

/**
 * Formate un montant en FCFA
 */
export function formatAmount(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount) + ' FCFA';
}

/**
 * Calcule le total à partir du billetage
 */
export function calculateDenominationTotal(denomination: Map<number, number>): number {
  let total = 0;
  denomination.forEach((count, value) => {
    total += value * count;
  });
  return total;
}

/**
 * Vérifie si un montant nécessite une confirmation supplémentaire
 */
export function requiresHighAmountConfirmation(amount: number): boolean {
  return amount >= WITHDRAWAL_THRESHOLDS.highAmountWarning;
}

/**
 * Vérifie si un montant nécessite validation superviseur
 */
export function requiresSupervisorApproval(amount: number): boolean {
  return amount >= WITHDRAWAL_THRESHOLDS.supervisorRequired;
}
